import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
} from '@mui/material';
import { Delete as DeleteIcon, Close as CloseIcon } from '@mui/icons-material';
import FullCalendar, {
  type DateClickInfo,
  type DatesSetInfo,
  type EventClickInfo,
  type EventInput,
} from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/react/daygrid';
import timeGridPlugin from '@fullcalendar/react/timegrid';
import interactionPlugin from '@fullcalendar/react/interaction';
// FullCalendar 7 ships no CSS of its own: the skeleton plus one theme (a plugin and
// its stylesheets) is what the v6 bundled look was. Classic is the v6 look.
import classicThemePlugin from '@fullcalendar/react/themes/classic';
import '@fullcalendar/react/skeleton.css';
import '@fullcalendar/react/themes/classic/theme.css';
import '@fullcalendar/react/themes/classic/palette.css';
import {
  PlannedFlight,
  PlannedFlightCreate,
  PlannedFlightUpdate,
  PlannedFlightStatus,
} from '../../../types/api';
import { usePlannedFlightsQuery, useCalendarReferenceDataQuery } from './calendar.queries';
import {
  useCreatePlannedFlightMutation,
  useUpdatePlannedFlightMutation,
  useDeletePlannedFlightMutation,
} from './calendar.mutations';
import { BRAND_COLORS } from '../../../styles/theme';

// Status color mapping
const STATUS_COLORS: Record<PlannedFlightStatus, string> = {
  planned: BRAND_COLORS.forestGreen.main,
  cancelled: '#9e9e9e',
  completed: '#4caf50',
};

// FullCalendar 7 hashes its own class names, so the tab's overrides hang off class
// names it hands to the calendar's *Class options (v7 adds them to the theme's own).
// The v6 `fontFamily: inherit` override is gone: no v7 stylesheet sets a font, so the
// calendar inherits it.
const CALENDAR_CLASSES = {
  toolbarTitle: 'xer-calendar-title',
  button: 'xer-calendar-button',
  event: 'xer-calendar-event',
  dayCell: 'xer-calendar-day',
  dayHeader: 'xer-calendar-day-header',
} as const;

// v7 gives every month row a minimum height of width / aspectRatio / 6, also with
// height="auto" (v6 sized rows to their events). 2.7 keeps an empty row at the v6
// height (~80px at the usual 1320px width); rows with more events still grow.
const MONTH_ROW_ASPECT_RATIO = 2.7;

const STATUS_LABELS: Record<PlannedFlightStatus, string> = {
  planned: 'Planned',
  cancelled: 'Cancelled',
  completed: 'Completed',
};

interface FlightFormData {
  planned_date: string;
  planned_end_date: string;
  drone_serial: string;
  pilot_id: string;
  customer_id: string;
  project_id: string;
  description: string;
  status: PlannedFlightStatus;
}

/** Convert an ISO 8601 (possibly TZ-aware) string to a local datetime-local value. */
const toLocalDatetimeString = (isoStr: string): string => {
  const date = new Date(isoStr);
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
};

const EMPTY_FORM: FlightFormData = {
  planned_date: '',
  planned_end_date: '',
  drone_serial: '',
  pilot_id: '',
  customer_id: '',
  project_id: '',
  description: '',
  status: 'planned',
};

export const CalendarTab: React.FC = () => {
  // Current visible date range (driven by FullCalendar onDatesSet)
  const [dateRange, setDateRange] = useState<{ from: string; to: string } | null>(null);

  // Server state via TanStack Query (STD-015)
  const flightsQuery = usePlannedFlightsQuery(dateRange?.from ?? null, dateRange?.to ?? null);
  const flights: PlannedFlight[] = useMemo(() => flightsQuery.data ?? [], [flightsQuery.data]);
  const loading = flightsQuery.isLoading;
  const error = flightsQuery.isError
    ? (flightsQuery.error as { userMessage?: string }).userMessage ?? 'Failed to load flights'
    : null;

  const refDataQuery = useCalendarReferenceDataQuery();
  const serialNumbers: string[] = refDataQuery.data?.serialNumbers ?? [];
  const pilots = refDataQuery.data?.pilots ?? [];
  const customers = refDataQuery.data?.customers ?? [];
  const projects = refDataQuery.data?.projects ?? [];

  // Mutations (STD-015)
  const createFlightMutation = useCreatePlannedFlightMutation();
  const updateFlightMutation = useUpdatePlannedFlightMutation();
  const deleteFlightMutation = useDeletePlannedFlightMutation();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFlight, setEditingFlight] = useState<PlannedFlight | null>(null);
  const [formData, setFormData] = useState<FlightFormData>(EMPTY_FORM);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const saving =
    createFlightMutation.isPending ||
    updateFlightMutation.isPending ||
    deleteFlightMutation.isPending;

  // Handle FullCalendar date range changes — just update the range;
  // the query re-runs automatically when from/to change.
  const handleDatesSet = useCallback((arg: DatesSetInfo) => {
    setDateRange({ from: arg.startStr, to: arg.endStr });
  }, []);

  // Convert flights to FullCalendar events
  const calendarEvents: EventInput[] = useMemo(
    () =>
      flights.map((flight) => ({
        id: flight.id,
        title: flight.description || (flight.drone_serial ? `SN${flight.drone_serial}` : 'Flight'),
        start: flight.planned_date,
        end: flight.planned_end_date || undefined,
        color: STATUS_COLORS[flight.status],
        extendedProps: { flight },
      })),
    [flights]
  );

  // Handle date click to create new flight
  const handleDateClick = useCallback((arg: DateClickInfo) => {
    setEditingFlight(null);
    setDialogError(null);
    setFormData({
      ...EMPTY_FORM,
      planned_date: arg.dateStr.includes('T') ? arg.dateStr : `${arg.dateStr}T09:00`,
    });
    setDialogOpen(true);
  }, []);

  // Handle event click to edit existing flight
  const handleEventClick = useCallback((arg: EventClickInfo) => {
    const flight = arg.event.extendedProps.flight as PlannedFlight;
    setEditingFlight(flight);
    setDialogError(null);
    setFormData({
      planned_date: toLocalDatetimeString(flight.planned_date),
      planned_end_date: flight.planned_end_date
        ? toLocalDatetimeString(flight.planned_end_date)
        : '',
      drone_serial: flight.drone_serial || '',
      pilot_id: flight.pilot_id || '',
      customer_id: flight.customer_id || '',
      project_id: flight.project_id || '',
      description: flight.description || '',
      status: flight.status,
    });
    setDialogOpen(true);
  }, []);

  // Handle form field changes
  const handleFieldChange = useCallback((field: keyof FlightFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Save (create or update)
  const handleSave = useCallback(async () => {
    setDialogError(null);

    try {
      if (editingFlight) {
        // Update
        const updateData: PlannedFlightUpdate = {};
        if (formData.planned_date) updateData.planned_date = formData.planned_date;
        if (formData.planned_end_date) updateData.planned_end_date = formData.planned_end_date;
        updateData.drone_serial = formData.drone_serial || null;
        updateData.pilot_id = formData.pilot_id || null;
        updateData.customer_id = formData.customer_id || null;
        updateData.project_id = formData.project_id || null;
        updateData.description = formData.description || null;
        if (formData.status !== editingFlight.status) updateData.status = formData.status;

        await updateFlightMutation.mutateAsync({ id: editingFlight.id, data: updateData });
      } else {
        // Create
        const createData: PlannedFlightCreate = {
          planned_date: formData.planned_date,
          planned_end_date: formData.planned_end_date || null,
          drone_serial: formData.drone_serial || null,
          pilot_id: formData.pilot_id || null,
          customer_id: formData.customer_id || null,
          project_id: formData.project_id || null,
          description: formData.description || null,
        };
        await createFlightMutation.mutateAsync(createData);
      }

      setDialogOpen(false);
    } catch (err) {
      const message =
        (err as { userMessage?: string }).userMessage || 'Failed to save planned flight';
      setDialogError(message);
    }
  }, [editingFlight, formData, createFlightMutation, updateFlightMutation]);

  // Delete flight
  const handleDelete = useCallback(async () => {
    if (!editingFlight) return;

    setDialogError(null);

    try {
      await deleteFlightMutation.mutateAsync(editingFlight.id);
      setDialogOpen(false);
    } catch (err) {
      const message =
        (err as { userMessage?: string }).userMessage || 'Failed to delete planned flight';
      setDialogError(message);
    }
  }, [editingFlight, deleteFlightMutation]);

  // Close dialog
  const handleClose = useCallback(() => {
    setDialogOpen(false);
    setEditingFlight(null);
    setDialogError(null);
  }, []);

  return (
    <Box sx={{ p: 2 }} data-testid="calendar-tab">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      <Box
        sx={{
          // Classic theme palette variable: v6 drew no background under the grid,
          // so the page colour showed through; v7's classic theme paints it white.
          '--fc-classic-background': 'transparent',
          [`& .${CALENDAR_CLASSES.dayHeader}`]: {
            fontWeight: 700,
          },
          [`& .${CALENDAR_CLASSES.toolbarTitle}`]: {
            fontSize: '1.25rem',
            fontWeight: 600,
          },
          [`& .${CALENDAR_CLASSES.button}`]: {
            textTransform: 'none',
            fontWeight: 500,
          },
          [`& .${CALENDAR_CLASSES.event}`]: {
            cursor: 'pointer',
            borderRadius: '4px',
            fontSize: '0.8rem',
            padding: '2px 4px',
          },
          [`& .${CALENDAR_CLASSES.dayCell}:hover`]: {
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
          },
        }}
      >
        <FullCalendar
          plugins={[classicThemePlugin, dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay',
          }}
          events={calendarEvents}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          datesSet={handleDatesSet}
          height="auto"
          editable={false}
          selectable={false}
          dayMaxEvents={4}
          eventDisplay="block"
          toolbarTitleClass={CALENDAR_CLASSES.toolbarTitle}
          buttonClass={CALENDAR_CLASSES.button}
          eventClass={CALENDAR_CLASSES.event}
          dayCellClass={CALENDAR_CLASSES.dayCell}
          dayHeaderClass={CALENDAR_CLASSES.dayHeader}
          aspectRatio={MONTH_ROW_ASPECT_RATIO}
          eventContent={(arg) => {
            const flight = arg.event.extendedProps.flight as PlannedFlight;
            return (
              <Box sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                {flight.drone_serial && (
                  <Chip
                    label={`SN${flight.drone_serial}`}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: '0.65rem',
                      mr: 0.5,
                      backgroundColor: 'rgba(255,255,255,0.3)',
                      color: 'inherit',
                    }}
                  />
                )}
                <Typography component="span" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                  {flight.description || 'Planned flight'}
                </Typography>
              </Box>
            );
          }}
        />
      </Box>

      {/* Readiness briefing placeholder */}
      <Box
        sx={{
          mt: 3,
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          backgroundColor: 'grey.50',
        }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          Readiness Briefing
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          No upcoming flights with drone assigned
        </Typography>
      </Box>

      {/* Create / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}
        data-testid="planned-flight-dialog"
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          {editingFlight ? 'Edit Planned Flight' : 'New Planned Flight'}
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}

          <TextField
            label="Planned Date"
            type="datetime-local"
            value={formData.planned_date}
            onChange={(e) => handleFieldChange('planned_date', e.target.value)}
            fullWidth
            required
            sx={{ mb: 2, mt: 1 }}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="End Date (optional)"
            type="datetime-local"
            value={formData.planned_end_date}
            onChange={(e) => handleFieldChange('planned_end_date', e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="planned-flight-drone-label">Drone</InputLabel>
            <Select
              labelId="planned-flight-drone-label"
              data-testid="planned-flight-drone"
              value={formData.drone_serial}
              label="Drone"
              onChange={(e) => handleFieldChange('drone_serial', e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {serialNumbers.map((sn) => (
                <MenuItem key={sn} value={sn}>
                  SN{sn}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Pilot</InputLabel>
            <Select
              value={formData.pilot_id}
              label="Pilot"
              onChange={(e) => handleFieldChange('pilot_id', e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {pilots.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.full_name || p.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Customer</InputLabel>
            <Select
              value={formData.customer_id}
              label="Customer"
              onChange={(e) => handleFieldChange('customer_id', e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {customers.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Project</InputLabel>
            <Select
              value={formData.project_id}
              label="Project"
              onChange={(e) => handleFieldChange('project_id', e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            fullWidth
            multiline
            rows={3}
            sx={{ mb: 2 }}
            inputProps={{ 'data-testid': 'planned-flight-description' }}
          />

          {editingFlight && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => handleFieldChange('status', e.target.value)}
              >
                {(Object.keys(STATUS_LABELS) as PlannedFlightStatus[]).map((s) => (
                  <MenuItem key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Box>
            {editingFlight && (
              <Button
                onClick={handleDelete}
                color="error"
                startIcon={<DeleteIcon />}
                disabled={saving}
              >
                Delete
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving || !formData.planned_date}
              data-testid="planned-flight-submit"
            >
              {saving ? <CircularProgress size={20} /> : editingFlight ? 'Save' : 'Create'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
